import { Breadcrumb, Button, Form, Modal, Input, InputNumber, Spin, Popconfirm, message, Space, Select, Switch, Radio, Card, Divider, Table, Tag, Descriptions } from "antd";
import Header from "../../components/dashboard/header";
import { DashboardOutlined, PlusSquareOutlined, EditOutlined, DeleteOutlined, PlusOutlined, MinusCircleOutlined, EyeOutlined } from "@ant-design/icons";
import { BsCalculator } from "react-icons/bs";
import { useState, useRef, useContext, useEffect } from "react"
import DataTable from "../../components/common/table";
import { Link, useNavigate } from "react-router-dom";
import RefreshContext from "../../context/refreshContext";
import { extractValueFromInputRef } from "../../helpers/dom";
import { useCreateDeduction, useDeleteDeduction, useGetAllDeductions, useUpdateDeduction, useGetAllLevels, useGetAllSteps } from "../../hooks/payrollData/payrollDataQuery";
import { useGetAllStaffCategories } from "../../hooks/staffcategory/staffCategoryQuery";
import { usePVTypes } from "../../hooks/voucherTypes/voucherTypeQuery";
import { hasPayrollAccess } from "../../helpers/roles";
import { userStore } from "../../store/userStore";

const { Option } = Select;

const COMPUTATION_TYPES = {
    FIXED: 'FIXED',
    PERCENTAGE: 'PERCENTAGE'
};

const SALARY_CALCULATION_BASE = {
    CONSOLIDATED: 'CONSOLIDATED',
    GROSS: 'GROSS'
};

// Validation helper for salary ranges
const validateSalaryRanges = (ranges) => {
    if (!ranges || ranges.length === 0) {
        return { valid: false, message: 'At least one salary range is required' };
    }

    // Sort ranges by minSalary for validation
    const sortedRanges = [...ranges].sort((a, b) => a.minSalary - b.minSalary);

    for (let i = 0; i < sortedRanges.length; i++) {
        const range = sortedRanges[i];

        // Validate individual range
        if (range.minSalary < 0 || range.maxSalary < 0) {
            return { valid: false, message: 'Salary ranges cannot have negative values' };
        }

        if (range.minSalary >= range.maxSalary) {
            return { valid: false, message: `Invalid range: Min salary (₦${range.minSalary.toLocaleString()}) must be less than Max salary (₦${range.maxSalary.toLocaleString()})` };
        }

        if (!range.isFixed && (range.deductionPercentage < 0 || range.deductionPercentage > 100)) {
            return { valid: false, message: 'Percentage must be between 0 and 100' };
        }

        if (range.isFixed && range.deductionAmount < 0) {
            return { valid: false, message: 'Deduction amount cannot be negative' };
        }

        // Check for overlapping ranges
        if (i > 0) {
            const prevRange = sortedRanges[i - 1];
            if (range.minSalary <= prevRange.maxSalary) {
                return { 
                    valid: false, 
                    message: `Overlapping ranges detected: ${prevRange.minSalary.toLocaleString()}-${prevRange.maxSalary.toLocaleString()} and ${range.minSalary.toLocaleString()}-${range.maxSalary.toLocaleString()}` 
                };
            }
        }
    }

    return { valid: true };
};

const deductionTableColumns = [
    {
        title: "S/N",
        key: "s/n",
        width: 60,
        render: (_, __, idx) => idx + 1
    },
    {
        title: "Title",
        key: "title",
        dataIndex: "deductionName",
        render: (text) => <strong>{text}</strong>
    },
    {
        title: "Type",
        key: "type",
        width: 120,
        render: (_, record) => (
            <Tag color={record.earnBased ? "purple" : "blue"}>
                {record.earnBased ? "Earn-Based" : "Standard"}
            </Tag>
        )
    },
    {
        title: "Value",
        key: "value",
        width: 100,
        render: (_, record) => {
            if (record.earnBased) {
                return <Tag color="orange">Variable</Tag>;
            }
            return record.computationType === 'PERCENTAGE' 
                ? `${record.deductionValue}%` 
                : `₦${record.deductionValue?.toLocaleString() || 0}`;
        }
    },
    {
        title: "Computation",
        key: "computationType",
        width: 120,
        render: (_, record) => (
            <Tag color={record.computationType === 'FIXED' ? "green" : "cyan"}>
                {record.computationType}
            </Tag>
        )
    },
    {
        title: "Salary Base",
        key: "salaryBase",
        width: 120,
        render: (_, record) => (
            <Tag color={record.salaryCalculationBase === 'CONSOLIDATED' ? "gold" : "geekblue"}>
                {record.salaryCalculationBase || 'GROSS'}
            </Tag>
        )
    },
    {
        title: "Application",
        key: "application",
        render: (_, record) => {
            if (record.appliesToAllUsers) {
                return <Tag color="green">All Users</Tag>;
            }
            const items = [];
            if (record.levels?.length) items.push(`${record.levels.length} Levels`);
            if (record.steps?.length) items.push(`${record.steps.length} Steps`);
            if (record.staffCategories?.length) items.push(`${record.staffCategories.length} Categories`);
            if (record.salaryStructure) items.push("Structure");
            return items.join(", ") || "-";
        }
    },
    {
        title: "Actions",
        key: "actions",
        width: 180,
        render: (_, deduction) => (<DeductionEdit deduction={deduction} />)
    }
]

// Salary Range Manager Component
function SalaryRangeManager({ salaryRanges, setSalaryRanges }) {
    const addRange = () => {
        const newRange = {
            id: Date.now(),
            minSalary: 0,
            maxSalary: 0,
            deductionAmount: 0,
            deductionPercentage: 0,
            isFixed: true
        };
        setSalaryRanges([...salaryRanges, newRange]);
    };

    const removeRange = (index) => {
        setSalaryRanges(salaryRanges.filter((_, i) => i !== index));
    };

    const updateRange = (index, field, value) => {
        const updated = [...salaryRanges];
        updated[index] = { ...updated[index], [field]: value };
        setSalaryRanges(updated);
    };

    const rangeColumns = [
        {
            title: 'Min Salary',
            dataIndex: 'minSalary',
            key: 'minSalary',
            width: 120,
            render: (_, record, index) => (
                <InputNumber
                    value={record.minSalary}
                    onChange={(value) => updateRange(index, 'minSalary', value)}
                    formatter={value => `₦ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/₦\s?|(,*)/g, '')}
                    style={{ width: '100%' }}
                    min={0}
                />
            )
        },
        {
            title: 'Max Salary',
            dataIndex: 'maxSalary',
            key: 'maxSalary',
            width: 120,
            render: (_, record, index) => (
                <InputNumber
                    value={record.maxSalary}
                    onChange={(value) => updateRange(index, 'maxSalary', value)}
                    formatter={value => `₦ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/₦\s?|(,*)/g, '')}
                    style={{ width: '100%' }}
                    min={0}
                />
            )
        },
        {
            title: 'Type',
            dataIndex: 'isFixed',
            key: 'isFixed',
            width: 100,
            render: (_, record, index) => (
                <Select
                    value={record.isFixed}
                    onChange={(value) => updateRange(index, 'isFixed', value)}
                    style={{ width: '100%' }}
                >
                    <Option value={true}>Fixed</Option>
                    <Option value={false}>Percentage</Option>
                </Select>
            )
        },
        {
            title: 'Value',
            dataIndex: 'value',
            key: 'value',
            width: 120,
            render: (_, record, index) => {
                if (record.isFixed) {
                    return (
                        <InputNumber
                            value={record.deductionAmount}
                            onChange={(value) => updateRange(index, 'deductionAmount', value)}
                            formatter={value => `₦ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/₦\s?|(,*)/g, '')}
                            style={{ width: '100%' }}
                            min={0}
                        />
                    );
                } else {
                    return (
                        <InputNumber
                            value={record.deductionPercentage}
                            onChange={(value) => updateRange(index, 'deductionPercentage', value)}
                            formatter={value => `${value}%`}
                            parser={value => value.replace('%', '')}
                            style={{ width: '100%' }}
                            min={0}
                            max={100}
                            step={0.1}
                        />
                    );
                }
            }
        },
        {
            title: 'Action',
            key: 'action',
            width: 60,
            render: (_, record, index) => (
                <Button
                    type="text"
                    danger
                    icon={<MinusCircleOutlined />}
                    onClick={() => removeRange(index)}
                    disabled={salaryRanges.length === 1}
                />
            )
        }
    ];

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 500 }}>Salary Ranges Configuration</span>
                <Button type="dashed" onClick={addRange} icon={<PlusOutlined />}>
                    Add Range
                </Button>
            </div>
            <Table
                columns={rangeColumns}
                dataSource={salaryRanges}
                pagination={false}
                size="small"
                rowKey={(record, index) => index}
                bordered
            />
        </div>
    );
}

function DeductionEdit({deduction}){
    const [isEditOpen,setIsEditOpen] = useState(false);
    const [isViewOpen,setIsViewOpen] = useState(false);
    const [levels, setLevels] = useState(deduction?.levels?.map(l => l.id) || []);
    const [steps, setSteps] = useState(deduction?.steps?.map(s => s.id) || []);
    const [staffCategories, setStaffCategories] = useState(deduction?.staffCategories?.map(sc => sc.id) || []);
    const [salaryStructure, setSalaryStructure] = useState(deduction?.salaryStructure);
    const [appliesToAllUsers, setAppliesToAllUsers] = useState(deduction?.appliesToAllUsers || false);
    const [computationType, setComputationType] = useState(deduction?.computationType || COMPUTATION_TYPES.FIXED);
    const [salaryCalculationBase, setSalaryCalculationBase] = useState(deduction?.salaryCalculationBase || SALARY_CALCULATION_BASE.GROSS);
    const [earnBased, setEarnBased] = useState(deduction?.earnBased || false);
    const [salaryRanges, setSalaryRanges] = useState(deduction?.salaryRanges?.length > 0 ? deduction.salaryRanges : []);

    const {flag,setFlag} = useContext(RefreshContext);
    const {levels: allLevels} = useGetAllLevels();
    const {steps: allSteps} = useGetAllSteps();
    const {staffCategories: allStaffCategories} = useGetAllStaffCategories();
    const {structures} = usePVTypes();

    const deleteDeduction = useDeleteDeduction();
    const updateDeduction = useUpdateDeduction();

    const titleRef = useRef(null);
    const valueRef = useRef(null);

    const confirmDeleteDeduction = async ()=>{
        await deleteDeduction(deduction.id);
        setFlag(!flag);
        message.success("Deduction deleted Successfully");
    }

    const handleSubmit = async ()=>{
        // Validation for earn-based deductions
        if (earnBased) {
            const validation = validateSalaryRanges(salaryRanges);
            if (!validation.valid) {
                message.error(validation.message);
                return;
            }
        }

        const payload = {
            deductionName:extractValueFromInputRef(titleRef),
            deductionValue: earnBased ? 0 : parseFloat(valueRef.current.value) || 0,
            computationType: computationType,
            salaryCalculationBase: salaryCalculationBase,
            earnBased: earnBased,
            salaryRanges: earnBased ? salaryRanges : [],
            levels: appliesToAllUsers ? [] : levels,
            steps: appliesToAllUsers ? [] : steps,
            staffCategories: appliesToAllUsers ? [] : staffCategories,
            salaryStructure: appliesToAllUsers ? null : (typeof salaryStructure === 'object' && salaryStructure !== null ? salaryStructure.id : salaryStructure),
            appliesToAllUsers: appliesToAllUsers
        }
        await updateDeduction(deduction.id,payload);
        message.success("Deduction updated successfully");
        setFlag(!flag);
        setIsEditOpen(false);
    }

    return (
        <>
              <Space size="small" wrap>
                <Button type="default" onClick={()=>setIsViewOpen(true)}>
                    <EyeOutlined/>
                    View
                </Button>
                <Button type="primary" onClick={()=>setIsEditOpen(true)}>
                    <EditOutlined/>
                    Edit
                </Button>
                <Popconfirm
                     title="Delete this deduction"
                     description="Are you sure to delete this ?"
                     onConfirm={confirmDeleteDeduction}
                    onCancel={()=>null}
                    okText="Yes"
                    cancelText="No"
                     >
                    <Button type="primary" danger>
                        <DeleteOutlined />
                        Delete
                    </Button>
                </Popconfirm>
            </Space>
            
            {/* View Modal */}
            <Modal 
                title="View Deduction Details" 
                open={isViewOpen} 
                footer={[
                    <Button key="close" onClick={() => setIsViewOpen(false)}>
                        Close
                    </Button>
                ]}
                onCancel={() => setIsViewOpen(false)}
                width={deduction?.earnBased ? 800 : 600}
            >
                <Descriptions bordered column={2} size="small">
                    <Descriptions.Item label="Deduction Name" span={2}>
                        <strong>{deduction?.deductionName}</strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="Type">
                        <Tag color={deduction?.earnBased ? "purple" : "blue"}>
                            {deduction?.earnBased ? "Earn-Based" : "Standard"}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Salary Base">
                        <Tag color={deduction?.salaryCalculationBase === 'CONSOLIDATED' ? "gold" : "geekblue"}>
                            {deduction?.salaryCalculationBase || 'GROSS'}
                        </Tag>
                    </Descriptions.Item>
                    {!deduction?.earnBased && (
                        <>
                            <Descriptions.Item label="Computation Type">
                                <Tag color={deduction?.computationType === 'FIXED' ? "green" : "cyan"}>
                                    {deduction?.computationType}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Value">
                                {deduction?.computationType === 'PERCENTAGE' 
                                    ? `${deduction?.deductionValue}%` 
                                    : `₦${deduction?.deductionValue?.toLocaleString() || 0}`}
                            </Descriptions.Item>
                        </>
                    )}
                    <Descriptions.Item label="Applies To All Users">
                        <Tag color={deduction?.appliesToAllUsers ? "green" : "orange"}>
                            {deduction?.appliesToAllUsers ? "Yes" : "No"}
                        </Tag>
                    </Descriptions.Item>
                    {!deduction?.appliesToAllUsers && (
                        <>
                            <Descriptions.Item label="Levels">
                                {deduction?.levels?.map(l => l.title).join(", ") || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Steps">
                                {deduction?.steps?.map(s => s.title).join(", ") || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Staff Categories">
                                {deduction?.staffCategories?.map(sc => sc.title).join(", ") || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Salary Structure">
                                {deduction?.salaryStructure?.title || "-"}
                            </Descriptions.Item>
                        </>
                    )}
                </Descriptions>
                
                {deduction?.earnBased && deduction?.salaryRanges?.length > 0 && (
                    <>
                        <Divider orientation="left">Salary Ranges</Divider>
                        <Table
                            columns={[
                                {
                                    title: 'Min Salary',
                                    dataIndex: 'minSalary',
                                    key: 'minSalary',
                                    render: (value) => `₦${value?.toLocaleString() || 0}`
                                },
                                {
                                    title: 'Max Salary',
                                    dataIndex: 'maxSalary',
                                    key: 'maxSalary',
                                    render: (value) => `₦${value?.toLocaleString() || 0}`
                                },
                                {
                                    title: 'Type',
                                    dataIndex: 'isFixed',
                                    key: 'isFixed',
                                    render: (isFixed) => (
                                        <Tag color={isFixed ? "green" : "cyan"}>
                                            {isFixed ? "Fixed" : "Percentage"}
                                        </Tag>
                                    )
                                },
                                {
                                    title: 'Value',
                                    key: 'value',
                                    render: (_, record) => {
                                        if (record.isFixed) {
                                            return `₦${record.deductionAmount?.toLocaleString() || 0}`;
                                        } else {
                                            return `${record.deductionPercentage || 0}%`;
                                        }
                                    }
                                }
                            ]}
                            dataSource={deduction?.salaryRanges}
                            pagination={false}
                            size="small"
                            rowKey={(record, index) => index}
                        />
                    </>
                )}
            </Modal>
            
            {/* Edit Modal */}
            <Modal 
                title="Update Deduction" 
                open={isEditOpen} 
                footer={null} 
                onCancel={()=>setIsEditOpen(false)}
                width={earnBased ? 1000 : 720}
            >
            <div style={{marginTop:"1em",maxHeight:"75vh",overflowY:"auto",padding:"0 10px"}}>
                    <Form 
                        wrapperCol={{ span: 24 }}
                        labelCol={{ span: 24 }}
                        layout="horizontal"
                        initialValues={{...deduction}}
                    >
                        <Card size="small" title="Basic Information" style={{ marginBottom: 12 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <Form.Item 
                                    name="deductionName"  
                                    label="Deduction Name"
                                    style={{ gridColumn: 'span 2', marginBottom: 12 }}
                                    rules={[
                                        {
                                          required: true,
                                          message:'Please enter Deduction Name'
                                        },
                                    ]}
                                >
                                    <Input
                                        ref={titleRef}
                                        placeholder="Enter Deduction Name"
                                    />
                                </Form.Item>

                                <Form.Item label="Deduction Type" style={{ marginBottom: 12 }}>
                                    <Radio.Group onChange={(e) => setEarnBased(e.target.value)} value={earnBased}>
                                        <Radio value={false}>Standard</Radio>
                                        <Radio value={true}>Earn-Based</Radio>
                                    </Radio.Group>
                                </Form.Item>

                                <Form.Item label="Salary Base" style={{ marginBottom: 12 }}>
                                    <Radio.Group onChange={(e) => setSalaryCalculationBase(e.target.value)} value={salaryCalculationBase}>
                                        <Radio value={SALARY_CALCULATION_BASE.GROSS}>Gross</Radio>
                                        <Radio value={SALARY_CALCULATION_BASE.CONSOLIDATED}>Consolidated</Radio>
                                    </Radio.Group>
                                </Form.Item>
                            </div>

                            {!earnBased && (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <Form.Item name="computationType" label="Computation Type" style={{ marginBottom: 0 }}>
                                        <Radio.Group onChange={(e) => setComputationType(e.target.value)} value={computationType}>
                                            <Radio value={COMPUTATION_TYPES.FIXED}>Fixed</Radio>
                                            <Radio value={COMPUTATION_TYPES.PERCENTAGE}>Percentage</Radio>
                                        </Radio.Group>
                                    </Form.Item>

                                    <Form.Item 
                                        name="deductionValue" 
                                        label={computationType === COMPUTATION_TYPES.FIXED ? "Amount" : "Percentage"}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <InputNumber 
                                            style={{width:"100%"}} 
                                            placeholder={computationType === COMPUTATION_TYPES.FIXED ? "Enter Amount" : "Enter %"} 
                                            ref={valueRef}
                                            formatter={computationType === COMPUTATION_TYPES.PERCENTAGE ? value => `${value}%` : value => `₦ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                            parser={computationType === COMPUTATION_TYPES.PERCENTAGE ? value => value.replace('%', '') : value => value.replace(/₦\s?|(,*)/g, '')}
                                            min={0}
                                            max={computationType === COMPUTATION_TYPES.PERCENTAGE ? 100 : undefined}
                                        />
                                    </Form.Item>
                                </div>
                            )}
                        </Card>

                        {earnBased && (
                            <Card size="small" title="Salary Ranges" style={{ marginBottom: 12 }}>
                                <SalaryRangeManager salaryRanges={salaryRanges} setSalaryRanges={setSalaryRanges} />
                            </Card>
                        )}

                        <Card size="small" title="Application Criteria" style={{ marginBottom: 12 }}>
                            <Form.Item name="appliesToAllUsers" valuePropName="checked" style={{ marginBottom: 12 }}>
                                <div style={{display: "flex", alignItems: "center"}}>
                                    <span style={{marginRight: "10px"}}>Applies To All Users:</span>
                                    <Switch onChange={(e)=>setAppliesToAllUsers(e)} checked={appliesToAllUsers} />
                                </div>
                            </Form.Item>
                            {!appliesToAllUsers && (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <Form.Item name="levels" label="Levels" style={{ marginBottom: 8 }}>
                                        <Select mode="multiple" allowClear placeholder="Select Levels" onChange={(e)=>setLevels(e)} value={levels}>
                                            {
                                                allLevels?.map((l,idx)=>(
                                                    <Option value={l.id} key={idx}>{l.title}</Option>
                                                ))
                                            }
                                        </Select>
                                    </Form.Item>
                                    <Form.Item name="steps" label="Steps" style={{ marginBottom: 8 }}>
                                        <Select mode="multiple" allowClear placeholder="Select Steps" onChange={(e)=>setSteps(e)} value={steps}>
                                            {
                                                allSteps?.map((s,idx)=>(
                                                    <Option value={s.id} key={idx}>{s.title} - {s?.level?.title}</Option>
                                                ))
                                            }
                                        </Select>
                                    </Form.Item>
                                    <Form.Item name="staffCategories" label="Staff Categories" style={{ marginBottom: 8 }}>
                                        <Select mode="multiple" allowClear placeholder="Select Staff Categories" onChange={(e)=>setStaffCategories(e)} value={staffCategories}>
                                            {
                                                allStaffCategories?.map((sc,idx)=>(
                                                    <Option value={sc.id} key={idx}>{sc.title}</Option>
                                                ))
                                            }
                                        </Select>
                                    </Form.Item>
                                    <Form.Item name="salaryStructure" label="Salary Structure" style={{ marginBottom: 0 }}>
                                        <Select allowClear placeholder="Select Salary Structure" onChange={(e)=>setSalaryStructure(e)}>
                                            {
                                                structures.map((s,idx)=>(
                                                    <Option value={s.id} key={idx}>{s.title}</Option>
                                                ))
                                            }
                                        </Select>
                                    </Form.Item>
                                </div>
                            )}
                        </Card>
                        <Form.Item style={{ marginTop: 16, textAlign: 'right' }}>
                            <Space>
                                <Button onClick={() => setIsEditOpen(false)}>
                                    Cancel
                                </Button>
                                <Button type="primary" onClick={handleSubmit}>
                                    Update Deduction
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </div>
            </Modal>
        </>
    )
}

export default function Deduction(){
    const navigate = useNavigate();
    const user = userStore(state => state.user);

    const [isOpen,setIsOpen] = useState(false);
    const [levels, setLevels] = useState([]);
    const [steps, setSteps] = useState([]);
    const [staffCategories, setStaffCategories] = useState([]);
    const [salaryStructure, setSalaryStructure] = useState(null);
    const [appliesToAllUsers, setAppliesToAllUsers] = useState(false);
    const [computationType, setComputationType] = useState(COMPUTATION_TYPES.FIXED);
    const [salaryCalculationBase, setSalaryCalculationBase] = useState(SALARY_CALCULATION_BASE.GROSS);
    const [earnBased, setEarnBased] = useState(false);
    const [salaryRanges, setSalaryRanges] = useState([]);

    // Check access permissions
    useEffect(() => {
        if (user && !hasPayrollAccess(user.roles)) {
            message.error('You do not have permission to access this page');
            navigate('/dashboard');
        }
    }, [user, navigate]);

    const titleRef = useRef(null);
    const valueRef = useRef(null);

    const showCreateModal = ()=>setIsOpen(true);
    const hideCreateModal = ()=>setIsOpen(false);

    const {flag,setFlag} = useContext(RefreshContext);

    const createDeduction = useCreateDeduction();
    const {loading,deductions} = useGetAllDeductions(flag);
    const {levels: allLevels} = useGetAllLevels();
    const {steps: allSteps} = useGetAllSteps();
    const {staffCategories: allStaffCategories} = useGetAllStaffCategories();
    const {structures} = usePVTypes();

    const handleSubmit = async ()=>{
        // Validation for earn-based deductions
        if (earnBased) {
            const validation = validateSalaryRanges(salaryRanges);
            if (!validation.valid) {
                message.error(validation.message);
                return;
            }
        }

        const payload = {
            deductionName:extractValueFromInputRef(titleRef),
            deductionValue: earnBased ? 0 : valueRef.current?.value || 0,
            computationType: computationType,
            salaryCalculationBase: salaryCalculationBase,
            earnBased: earnBased,
            salaryRanges: earnBased ? salaryRanges : [],
            levels: appliesToAllUsers ? [] : levels,
            steps: appliesToAllUsers ? [] : steps,
            staffCategories: appliesToAllUsers ? [] : staffCategories,
            salaryStructure: appliesToAllUsers ? null : (typeof salaryStructure === 'object' && salaryStructure !== null ? salaryStructure.id : salaryStructure),
            appliesToAllUsers: appliesToAllUsers
        }
        await createDeduction(payload);
        setFlag(!flag);
        message.success("Deduction created successfully");
        
        // Reset form
        setIsOpen(false);
        setEarnBased(false);
        setSalaryRanges([]);
        setSalaryCalculationBase(SALARY_CALCULATION_BASE.GROSS);
        setComputationType(COMPUTATION_TYPES.FIXED);
        setLevels([]);
        setSteps([]);
        setStaffCategories([]);
        setSalaryStructure(null);
        setAppliesToAllUsers(false);
    }

    return (
        <>
        <div className="main-container">
            <Header/>
            <div className="main-body">
                <div className="info-bar">
                <Breadcrumb>
                            <Breadcrumb.Item>
                            <DashboardOutlined className="dashboard-icon"/>
                            <Link to={"/dashboard"}>
                                Dashboard
                            </Link>
                            </Breadcrumb.Item>
                            <Breadcrumb.Item>
                                <BsCalculator/>
                                <Link to="/dashboard/deduction">
                                    Deduction
                                </Link>
                            </Breadcrumb.Item>
                        </Breadcrumb>
                        <div style={{display:'flex',gap:'1em'}}>
                        <Button style={{backgroundColor:"green",color:"#FFFFFF"}} onClick={showCreateModal}>
                                <PlusSquareOutlined/>
                                Add Deduction
                            </Button>
                        </div>
                </div>
                <div className="table-section">
                        <Spin spinning={loading}>
                            <DataTable cols={deductionTableColumns} data={deductions}/>
                        </Spin>
                    </div>
            </div>
        </div>
        <Modal 
            title="Add Deduction" 
            open={isOpen} 
            footer={null} 
            onCancel={hideCreateModal}
            width={earnBased ? 1000 : 720}
        >
            <div style={{marginTop:"1em",maxHeight:"75vh",overflowY:"auto",padding:"0 10px"}}>
                <Form 
                    wrapperCol={{ span: 24 }}
                    labelCol={{ span: 24 }}
                    layout="horizontal"
                >
                    <Card size="small" title="Basic Information" style={{ marginBottom: 12 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <Form.Item 
                                label="Deduction Name"
                                style={{ gridColumn: 'span 2', marginBottom: 12 }}
                                rules={[
                                    {
                                      required: true,
                                      message:'Please enter Deduction Name'
                                    },
                                ]}
                            >
                                <Input
                                    ref={titleRef}
                                    placeholder="Enter Deduction Name"
                                />
                            </Form.Item>

                            <Form.Item label="Deduction Type" style={{ marginBottom: 12 }}>
                                <Radio.Group onChange={(e) => {
                                    setEarnBased(e.target.value);
                                    if (e.target.value) {
                                        // Initialize with one default range for earn-based
                                        setSalaryRanges([{
                                            id: Date.now(),
                                            minSalary: 0,
                                            maxSalary: 100000,
                                            deductionAmount: 0,
                                            deductionPercentage: 0,
                                            isFixed: true
                                        }]);
                                    } else {
                                        setSalaryRanges([]);
                                    }
                                }} value={earnBased}>
                                    <Radio value={false}>Standard</Radio>
                                    <Radio value={true}>Earn-Based</Radio>
                                </Radio.Group>
                            </Form.Item>

                            <Form.Item label="Salary Base" style={{ marginBottom: 12 }}>
                                <Radio.Group onChange={(e) => setSalaryCalculationBase(e.target.value)} value={salaryCalculationBase}>
                                    <Radio value={SALARY_CALCULATION_BASE.GROSS}>Gross</Radio>
                                    <Radio value={SALARY_CALCULATION_BASE.CONSOLIDATED}>Consolidated</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </div>

                        {!earnBased && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <Form.Item label="Computation Type" style={{ marginBottom: 0 }}>
                                    <Radio.Group onChange={(e) => setComputationType(e.target.value)} value={computationType}>
                                        <Radio value={COMPUTATION_TYPES.FIXED}>Fixed</Radio>
                                        <Radio value={COMPUTATION_TYPES.PERCENTAGE}>Percentage</Radio>
                                    </Radio.Group>
                                </Form.Item>

                                <Form.Item 
                                    label={computationType === COMPUTATION_TYPES.FIXED ? "Amount" : "Percentage"}
                                    style={{ marginBottom: 0 }}
                                >
                                    <InputNumber 
                                        style={{width:"100%"}} 
                                        placeholder={computationType === COMPUTATION_TYPES.FIXED ? "Enter Amount" : "Enter %"} 
                                        ref={valueRef}
                                        formatter={computationType === COMPUTATION_TYPES.PERCENTAGE ? value => `${value}%` : value => `₦ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                        parser={computationType === COMPUTATION_TYPES.PERCENTAGE ? value => value.replace('%', '') : value => value.replace(/₦\s?|(,*)/g, '')}
                                        min={0}
                                        max={computationType === COMPUTATION_TYPES.PERCENTAGE ? 100 : undefined}
                                    />
                                </Form.Item>
                            </div>
                        )}
                    </Card>

                    {earnBased && (
                        <Card size="small" title="Salary Ranges" style={{ marginBottom: 12 }}>
                            <SalaryRangeManager salaryRanges={salaryRanges} setSalaryRanges={setSalaryRanges} />
                        </Card>
                    )}

                    <Card size="small" title="Application Criteria" style={{ marginBottom: 12 }}>
                        <Form.Item style={{ marginBottom: 12 }}>
                            <div style={{display: "flex", alignItems: "center"}}>
                                <span style={{marginRight: "10px"}}>Applies To All Users:</span>
                                <Switch onChange={(e)=>setAppliesToAllUsers(e)} checked={appliesToAllUsers} />
                            </div>
                        </Form.Item>

                        {!appliesToAllUsers && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <Form.Item label="Levels" style={{ marginBottom: 8 }}>
                                    <Select mode="multiple" allowClear placeholder="Select Levels" onChange={(e)=>setLevels(e)} value={levels}>
                                        {
                                            allLevels?.map((l,idx)=>(
                                                <Option value={l.id} key={idx}>{l.title}</Option>
                                            ))
                                        }
                                    </Select>
                                </Form.Item>
                                <Form.Item label="Steps" style={{ marginBottom: 8 }}>
                                    <Select mode="multiple" allowClear placeholder="Select Steps" onChange={(e)=>setSteps(e)} value={steps}>
                                        {
                                            allSteps?.map((s,idx)=>(
                                                <Option value={s.id} key={idx}>{s.title} - {s?.level?.title}</Option>
                                            ))
                                        }
                                    </Select>
                                </Form.Item>
                                <Form.Item label="Staff Categories" style={{ marginBottom: 8 }}>
                                    <Select mode="multiple" allowClear placeholder="Select Staff Categories" onChange={(e)=>setStaffCategories(e)} value={staffCategories}>
                                        {
                                            allStaffCategories?.map((sc,idx)=>(
                                                <Option value={sc.id} key={idx}>{sc.title}</Option>
                                            ))
                                        }
                                    </Select>
                                </Form.Item>
                                <Form.Item label="Salary Structure" style={{ marginBottom: 0 }}>
                                    <Select allowClear placeholder="Select Salary Structure" onChange={(e)=>setSalaryStructure(e)} value={salaryStructure}>
                                        {
                                            structures.map((s,idx)=>(
                                                <Option value={s.id} key={idx}>{s.title}</Option>
                                            ))
                                        }
                                    </Select>
                                </Form.Item>
                            </div>
                        )}
                    </Card>

                    <Form.Item style={{ marginTop: 16, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={hideCreateModal}>
                                Cancel
                            </Button>
                            <Button type="primary" onClick={handleSubmit}>
                                Create Deduction
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </div>
        </Modal>
        </>
    )
}
